import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import type { EntityRef } from "@/api/types";
import { shortId } from "@/lib/formatters";
import { cn } from "@/lib/utils";

/**
 * Typed deep links between resources. Every link uses a real id from an API response, which
 * is what makes the Organization → … → Purchase trace navigable.
 */
const cls = "text-foreground underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none";

export function OrgLink({ org, className }: { org: EntityRef | null | undefined; className?: string }) {
  if (!org) return <span className="text-muted-foreground">—</span>;
  return (
    <Link to="/organizations/$organizationId" params={{ organizationId: org.id }} className={cn(cls, className)}>
      {org.name}
    </Link>
  );
}

export function StoreLink({ store, className }: { store: EntityRef | null | undefined; className?: string }) {
  if (!store) return <span className="text-muted-foreground">—</span>;
  return (
    <Link to="/stores/$storeId" params={{ storeId: store.id }} className={cn(cls, className)}>
      {store.name}
    </Link>
  );
}

export function UserLink({ user, className }: { user: EntityRef | null | undefined; className?: string }) {
  if (!user) return <span className="text-muted-foreground">—</span>;
  return (
    <Link to="/users/$userId" params={{ userId: user.id }} className={cn(cls, className)}>
      {user.name}
    </Link>
  );
}

function IdLink({ children, className, title }: { children: ReactNode; className?: string; title: string }) {
  return (
    <span className={cn("whitespace-nowrap font-mono text-[11.5px]", className)} title={title}>
      {children}
    </span>
  );
}

export function OpportunityLink({ id, label, className }: { id: string | null | undefined; label?: string | null; className?: string }) {
  if (!id) return <span className="text-muted-foreground">—</span>;
  return (
    <Link to="/opportunities/$opportunityId" params={{ opportunityId: id }} className={cn(cls, className)}>
      {label ?? <IdLink title={id}>{shortId(id)}</IdLink>}
    </Link>
  );
}

export function ConversationLink({ id, label, className }: { id: string | null | undefined; label?: string | null; className?: string }) {
  if (!id) return <span className="text-muted-foreground">—</span>;
  return (
    <Link to="/conversations/$conversationId" params={{ conversationId: id }} className={cn(cls, className)}>
      {label ?? <IdLink title={id}>{shortId(id)}</IdLink>}
    </Link>
  );
}

export function AiRunLink({ id, label, className }: { id: string | null | undefined; label?: string | null; className?: string }) {
  if (!id) return <span className="text-muted-foreground">—</span>;
  return (
    <Link to="/ai/runs/$aiRunId" params={{ aiRunId: id }} className={cn(cls, className)}>
      {label ?? <IdLink title={id}>{shortId(id)}</IdLink>}
    </Link>
  );
}

export function JobLink({ queue, id, className }: { queue: string | null | undefined; id: string | null | undefined; className?: string }) {
  if (!queue || !id) return <span className="text-muted-foreground">—</span>;
  return (
    <Link to="/queues/$queue/jobs/$jobId" params={{ queue, jobId: id }} className={cn(cls, className)}>
      <IdLink title={`${queue} #${id}`}>
        {queue}#{id}
      </IdLink>
    </Link>
  );
}

export function IntegrationLink({ id, name, className }: { id: string; name: string; className?: string }) {
  return (
    <Link to="/integrations/$integrationId" params={{ integrationId: id }} className={cn(cls, className)}>
      {name}
    </Link>
  );
}
